import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider } from "./context/AuthContext";
import { LoaderProvider } from "./context/LoaderContext";
import Loader from "./components/Loader";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UsersPage from "./pages/UsersPage";
import VendorsPage from "./pages/VendorsPage";
import ClientsPage from "./pages/ClientsPage";
import CategoriesPage from "./pages/CategoriesPage";
import InventoryPage from "./pages/InventoryPage";
import PurchasesPage from "./pages/PurchasesPage";
import PurchaseInvoicePage from "./pages/PurchaseInvoicePage";
import SalesPage from "./pages/SalesPage";
import SaleInvoicePage from "./pages/SaleInvoicePage";
import StockPage from "./pages/StockPage";
import WarrantyPage from "./pages/WarrantyPage";
import ReportsPage from "./pages/ReportsPage";
import ScanPage from "./pages/ScanPage";

function App() {
  return (
    <AuthProvider>
      <LoaderProvider>
        <div className="App">
          <Loader />

          {/* Global Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={3500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />

          {/* Application Routing */}
          <Routes>
            {/* Public Login Route */}
            <Route path="/login" element={<Login />} />

            {/* Protected App Layout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute permission="dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Admin Only Route (Requirement 3 & 4) */}
              <Route
                path="users"
                element={
                  <ProtectedRoute adminOnly>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />

              {/* Vendor Module (Requirement 7) */}
              <Route
                path="vendors"
                element={
                  <ProtectedRoute permission="vendors">
                    <VendorsPage />
                  </ProtectedRoute>
                }
              />

              {/* Client Module (Requirement 8) */}
              <Route
                path="clients"
                element={
                  <ProtectedRoute permission="clients">
                    <ClientsPage />
                  </ProtectedRoute>
                }
              />

              {/* Category & Subcategory Pre-requisite (Requirement 9.1) */}
              <Route
                path="categories"
                element={
                  <ProtectedRoute permission="categories">
                    <CategoriesPage />
                  </ProtectedRoute>
                }
              />

              {/* Inventory Module (Requirement 9.2) */}
              <Route
                path="inventory"
                element={
                  <ProtectedRoute permission="inventory">
                    <InventoryPage />
                  </ProtectedRoute>
                }
              />

              {/* Purchase Module (Requirement 10) */}
              <Route
                path="purchases"
                element={
                  <ProtectedRoute permission="purchases">
                    <PurchasesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="purchases/invoice/:billNumber"
                element={
                  <ProtectedRoute permission="purchases">
                    <PurchaseInvoicePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="purchases/:billNumber"
                element={
                  <ProtectedRoute permission="purchases">
                    <PurchaseInvoicePage />
                  </ProtectedRoute>
                }
              />

              {/* Sales Module (Requirement 11) */}
              <Route
                path="sales"
                element={
                  <ProtectedRoute permission="sales">
                    <SalesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales/invoice/:invoiceNumber"
                element={
                  <ProtectedRoute permission="sales">
                    <SaleInvoicePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales/:invoiceNumber"
                element={
                  <ProtectedRoute permission="sales">
                    <SaleInvoicePage />
                  </ProtectedRoute>
                }
              />

              {/* Stock Module — Available Stock */}
              <Route
                path="stock"
                element={
                  <ProtectedRoute permission="stock">
                    <StockPage />
                  </ProtectedRoute>
                }
              />

              {/* Scan Barcode & Serial Module (Device & Camera) */}
              <Route
                path="scan"
                element={
                  <ProtectedRoute superAdminOnly>
                    <ScanPage />
                  </ProtectedRoute>
                }
              />

              {/* Warranty Tracker Module — Full Page */}
              <Route
                path="warranty"
                element={
                  <ProtectedRoute permission="warranty">
                    <WarrantyPage />
                  </ProtectedRoute>
                }
              />

              {/* Tracking & Reporting Module (Admin Only) */}
              <Route
                path="reports"
                element={
                  <ProtectedRoute adminOnly>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </LoaderProvider>
    </AuthProvider>
  );
}

export default App;
